import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Settings2,
  DollarSign,
  Clock,
  Target,
  Trophy,
  Shield,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Info,
  Sparkles,
  FileText,
  Stethoscope,
  ListChecks,
  Search,
  Crown,
  TrendingUp,
  Rocket,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { ModelLogo, getModelName } from '../../components/ModelLogo';
import { LoadingState, Tooltip } from '../../components/ui';

// ============================================================================
// TYPES
// ============================================================================

interface ModelComparisonResponse {
  data: {
    models: Array<{
      model: string;
      totalExecutions: number;
      successfulExecutions: number;
      averageIndicationLatency: number;
      averageCriteriaLatency: number;
      totalCost: number;
      verdictDistribution: Record<string, number>;
      selectedBestCount: number;
      winRate: string | number;
    }>;
    totalBestSelections: number;
  };
}

interface SearchModelComparisonResponse {
  data: {
    models: Array<{
      model: string;
      totalExecutions: number;
      successfulExecutions: number;
      averageLatencyMs: number;
      averageCostUsd: number;
      averageRelevanceScore: number;
      averageCombinedScore: number;
      verdictDistribution: Record<string, number>;
      selectedBestCount: number;
      winRate: string | number;
    }>;
    totalBestSelections: number;
    totalSearchEvaluations: number;
  };
}

interface QualityAssessmentResponse {
  data: {
    summary: {
      totalAssessmentSets: number;
      totalProvidersAssessed: number;
      averageTotalScore: number;
      goodMatchCount: number;
      goodMatchRate: number;
    };
    modelPerformance: Array<{
      model: string;
      totalAssessments: number;
      averageQualityScore: number;
      goodMatchCount: number;
      goodMatchRate: number;
    }>;
    aiAssessment?: {
      modelPerformance: Array<{
        model: string;
        totalAssessments: number;
        averageQualityScore: number;
        goodMatchCount: number;
        goodMatchRate: number;
      }>;
    };
  };
}

interface StabilityAnalyticsResponse {
  data: {
    modelPerformance: Array<{
      model: string;
      totalRuns: number;
      completedRuns: number;
      phase1: {
        humanEvaluations: number;
        verdictDistribution: Record<string, number>;
        aiAnalyses: number;
        averageConsistencyScore: number | null;
      };
      phase2: {
        hasSearchPhase: number;
        humanEvaluations: number;
        verdictDistribution: Record<string, number>;
        aiAnalyses: number;
        averageConsistencyScore: number | null;
      };
    }>;
  };
}

interface CriteriaWeights {
  quality: number;
  stability: number;
  winRate: number;
  cost: number;
  latency: number;
}

interface AggregatedModelScore {
  model: string;
  // Raw metrics
  qualityScore: number | null;
  stabilityScore: number | null;
  winRate: number | null;
  avgCost: number | null;
  avgLatency: number | null;
  // Phase-specific
  phase23WinRate: number | null;
  phase4WinRate: number | null;
  phase1Stability: number | null;
  phase4Stability: number | null;
  // Computed
  weightedScore: number;
  confidence: number; // Based on data availability
}

interface WorkflowPhase {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  dataSource: 'phase23' | 'phase4' | 'combined';
}

// ============================================================================
// CONSTANTS
// ============================================================================

const WORKFLOW_PHASES: WorkflowPhase[] = [
  {
    id: 'questionnaire',
    name: 'Questionnaire',
    description: 'Generate tailored medical questions',
    icon: FileText,
    color: 'blue',
    dataSource: 'phase23',
  },
  {
    id: 'indication',
    name: 'Indication Analysis',
    description: 'Analyze medical appropriateness',
    icon: Stethoscope,
    color: 'purple',
    dataSource: 'phase23',
  },
  {
    id: 'criteria',
    name: 'Criteria Generation',
    description: 'Create prioritized search criteria',
    icon: ListChecks,
    color: 'amber',
    dataSource: 'phase23',
  },
  {
    id: 'search',
    name: 'Provider Matching',
    description: 'Find and rank care providers',
    icon: Search,
    color: 'green',
    dataSource: 'phase4',
  },
];

const DEFAULT_WEIGHTS: CriteriaWeights = {
  quality: 30,
  stability: 25,
  winRate: 25,
  cost: 10,
  latency: 10,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function normalizeScore(value: number | null, min: number, max: number, invert = false): number {
  if (value === null) return 0;
  const normalized = (value - min) / (max - min);
  const clamped = Math.max(0, Math.min(1, normalized));
  return invert ? 1 - clamped : clamped;
}

function calculateWeightedScore(
  model: AggregatedModelScore,
  weights: CriteriaWeights,
  phase: 'phase23' | 'phase4' | 'combined'
): number {
  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
  if (totalWeight === 0) return 0;

  let score = 0;
  let appliedWeight = 0;

  // Quality (higher is better)
  if (model.qualityScore !== null) {
    score += (model.qualityScore / 100) * weights.quality;
    appliedWeight += weights.quality;
  }

  // Stability (higher is better)
  const stabilityValue = phase === 'phase4' ? model.phase4Stability : model.phase1Stability;
  if (stabilityValue !== null) {
    score += (stabilityValue / 100) * weights.stability;
    appliedWeight += weights.stability;
  }

  // Win Rate (higher is better)
  const winRateValue = phase === 'phase4' ? model.phase4WinRate : model.phase23WinRate;
  if (winRateValue !== null) {
    score += (winRateValue / 100) * weights.winRate;
    appliedWeight += weights.winRate;
  }

  // Cost (lower is better - invert)
  if (model.avgCost !== null) {
    const costNorm = normalizeScore(model.avgCost, 0, 1, true); // Assume max $1 per call
    score += costNorm * weights.cost;
    appliedWeight += weights.cost;
  }

  // Latency (lower is better - invert)
  if (model.avgLatency !== null) {
    const latencyNorm = normalizeScore(model.avgLatency, 0, 60000, true); // Assume max 60s
    score += latencyNorm * weights.latency;
    appliedWeight += weights.latency;
  }

  // Normalize by actually applied weight
  return appliedWeight > 0 ? (score / appliedWeight) * totalWeight : 0;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-600';
}

function getScoreBgColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-blue-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

function calculateStabilityRate(verdictDist: Record<string, number>): number | null {
  const total = Object.values(verdictDist).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  const stable = (verdictDist['STABLE'] || 0) + (verdictDist['MOSTLY_STABLE'] || 0);
  return Math.round((stable / total) * 100);
}

// ============================================================================
// COMPONENTS
// ============================================================================

interface WeightSliderProps {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  onChange: (value: number) => void;
  color: string;
}

function WeightSlider({ label, icon: Icon, value, onChange, color }: WeightSliderProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={clsx('w-4 h-4', color)} />
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className="text-sm font-bold text-gray-900">{value}%</span>
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
      />
    </div>
  );
}

interface WorkflowNodeProps {
  phase: WorkflowPhase;
  recommendedModel: AggregatedModelScore | null;
  score: number;
  isLast: boolean;
  onClick: () => void;
  isSelected: boolean;
}

function WorkflowNode({ phase, recommendedModel, score, isLast, onClick, isSelected }: WorkflowNodeProps) {
  const Icon = phase.icon;
  const colorMap: Record<string, { bg: string; border: string; iconBg: string }> = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-500' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', iconBg: 'bg-purple-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-500' },
    green: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500' },
  };
  const colors = colorMap[phase.color] || colorMap.blue;

  return (
    <div className="flex items-center">
      <button
        onClick={onClick}
        className={clsx(
          'relative rounded-xl border-2 p-4 transition-all hover:shadow-lg cursor-pointer min-w-[200px]',
          colors.bg,
          isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : '',
          colors.border
        )}
      >
        {/* Phase Icon */}
        <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center mb-3', colors.iconBg)}>
          <Icon className="w-5 h-5 text-white" />
        </div>

        {/* Phase Info */}
        <h3 className="font-semibold text-gray-900 mb-1">{phase.name}</h3>
        <p className="text-xs text-gray-500 mb-3">{phase.description}</p>

        {/* Recommended Model */}
        {recommendedModel ? (
          <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100">
            <ModelLogo model={recommendedModel.model} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {getModelName(recommendedModel.model)}
              </p>
              <p className={clsx('text-xs font-bold', getScoreColor(score))}>
                Score: {score.toFixed(1)}
              </p>
            </div>
            <Crown className="w-4 h-4 text-amber-500" />
          </div>
        ) : (
          <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">No data</span>
          </div>
        )}
      </button>

      {/* Arrow */}
      {!isLast && (
        <div className="flex items-center px-2">
          <div className="w-8 h-0.5 bg-gray-300" />
          <ArrowRight className="w-5 h-5 text-gray-400 -ml-1" />
        </div>
      )}
    </div>
  );
}

interface ModelRankingCardProps {
  rank: number;
  model: AggregatedModelScore;
  weights: CriteriaWeights;
  phase: 'phase23' | 'phase4' | 'combined';
  isRecommended: boolean;
}

function ModelRankingCard({ rank, model, weights, phase, isRecommended }: ModelRankingCardProps) {
  const score = calculateWeightedScore(model, weights, phase);

  return (
    <div
      className={clsx(
        'p-4 rounded-lg border transition-all',
        isRecommended
          ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 ring-2 ring-amber-300'
          : 'bg-white border-gray-200 hover:border-gray-300'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Rank Badge */}
        <div
          className={clsx(
            'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm',
            rank === 1 ? 'bg-amber-100 text-amber-700' :
            rank === 2 ? 'bg-gray-200 text-gray-600' :
            rank === 3 ? 'bg-orange-100 text-orange-700' :
            'bg-gray-100 text-gray-500'
          )}
        >
          {rank === 1 ? <Crown className="w-4 h-4" /> : rank}
        </div>

        {/* Model Info */}
        <ModelLogo model={model.model} size="md" />
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{getModelName(model.model)}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={clsx('text-lg font-bold', getScoreColor(score))}>
              {score.toFixed(1)}
            </span>
            <span className="text-xs text-gray-500">weighted score</span>
          </div>
        </div>

        {/* Confidence indicator */}
        <Tooltip content={`${model.confidence}% data coverage`}>
          <div className="flex items-center gap-1">
            <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full', getScoreBgColor(model.confidence))}
                style={{ width: `${model.confidence}%` }}
              />
            </div>
          </div>
        </Tooltip>
      </div>

      {/* Metric Breakdown */}
      <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-5 gap-2 text-center">
        <div>
          <p className="text-xs text-gray-500">Quality</p>
          <p className={clsx('text-sm font-semibold', model.qualityScore !== null ? getScoreColor(model.qualityScore) : 'text-gray-400')}>
            {model.qualityScore !== null ? `${model.qualityScore.toFixed(0)}%` : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Stability</p>
          <p className={clsx('text-sm font-semibold',
            phase === 'phase4'
              ? (model.phase4Stability !== null ? getScoreColor(model.phase4Stability) : 'text-gray-400')
              : (model.phase1Stability !== null ? getScoreColor(model.phase1Stability) : 'text-gray-400')
          )}>
            {phase === 'phase4'
              ? (model.phase4Stability !== null ? `${model.phase4Stability.toFixed(0)}%` : 'N/A')
              : (model.phase1Stability !== null ? `${model.phase1Stability.toFixed(0)}%` : 'N/A')}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Win Rate</p>
          <p className={clsx('text-sm font-semibold',
            phase === 'phase4'
              ? (model.phase4WinRate !== null ? getScoreColor(model.phase4WinRate) : 'text-gray-400')
              : (model.phase23WinRate !== null ? getScoreColor(model.phase23WinRate) : 'text-gray-400')
          )}>
            {phase === 'phase4'
              ? (model.phase4WinRate !== null ? `${model.phase4WinRate.toFixed(0)}%` : 'N/A')
              : (model.phase23WinRate !== null ? `${model.phase23WinRate.toFixed(0)}%` : 'N/A')}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Avg Cost</p>
          <p className="text-sm font-semibold text-gray-700">
            {model.avgCost !== null ? `$${model.avgCost.toFixed(3)}` : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Latency</p>
          <p className="text-sm font-semibold text-gray-700">
            {model.avgLatency !== null ? `${(model.avgLatency / 1000).toFixed(1)}s` : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DEPLOY MODAL
// ============================================================================

interface DeployModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeploying: boolean;
  deploySuccess: boolean;
  configuration: Array<{
    phase: string;
    model: string | null;
    score: number;
  }>;
  weights: CriteriaWeights;
}

function DeployModal({
  isOpen,
  onClose,
  onConfirm,
  isDeploying,
  deploySuccess,
  configuration,
  weights,
}: DeployModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Deploy to Production</h2>
                <p className="text-sm text-blue-100">Review configuration before deployment</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {deploySuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Deployment Successful!</h3>
              <p className="text-gray-500 mb-6">
                Your production configuration has been updated.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Configuration Summary */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Model Configuration</h3>
                <div className="space-y-2">
                  {configuration.map((item) => (
                    <div
                      key={item.phase}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm text-gray-600">{item.phase}</span>
                      {item.model ? (
                        <div className="flex items-center gap-2">
                          <ModelLogo model={item.model} size="sm" />
                          <span className="font-medium text-gray-900">
                            {getModelName(item.model)}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({item.score.toFixed(1)})
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not configured</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Weights Summary */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Criteria Weights</h3>
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <p className="text-xs text-gray-500">Quality</p>
                    <p className="font-bold text-green-600">{weights.quality}%</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <p className="text-xs text-gray-500">Stability</p>
                    <p className="font-bold text-blue-600">{weights.stability}%</p>
                  </div>
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <p className="text-xs text-gray-500">Win Rate</p>
                    <p className="font-bold text-amber-600">{weights.winRate}%</p>
                  </div>
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <p className="text-xs text-gray-500">Cost</p>
                    <p className="font-bold text-purple-600">{weights.cost}%</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg">
                    <p className="text-xs text-gray-500">Latency</p>
                    <p className="font-bold text-red-600">{weights.latency}%</p>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Production Deployment</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This will update the production environment. Changes will affect all users immediately.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  disabled={isDeploying}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isDeploying}
                  className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeploying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deploying...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4" />
                      Deploy Now
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ProductionWorkflow() {
  const [weights, setWeights] = useState<CriteriaWeights>(DEFAULT_WEIGHTS);
  const [selectedPhase, setSelectedPhase] = useState<string>('questionnaire');
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploySuccess, setDeploySuccess] = useState(false);

  // Fetch all analytics data
  const { data: modelComparison, isLoading: loadingModels } = useQuery({
    queryKey: ['analytics', 'models'],
    queryFn: () => api.get<ModelComparisonResponse>('/api/validation/analytics/models'),
  });

  const { data: searchComparison, isLoading: loadingSearch } = useQuery({
    queryKey: ['analytics', 'search-models'],
    queryFn: () => api.get<SearchModelComparisonResponse>('/api/validation/analytics/search-models'),
  });

  const { data: qualityData, isLoading: loadingQuality } = useQuery({
    queryKey: ['analytics', 'quality-assessment'],
    queryFn: () => api.get<QualityAssessmentResponse>('/api/validation/analytics/quality-assessment'),
  });

  const { data: stabilityData, isLoading: loadingStability } = useQuery({
    queryKey: ['analytics', 'stability'],
    queryFn: () => api.get<StabilityAnalyticsResponse>('/api/validation/analytics/stability'),
  });

  const isLoading = loadingModels || loadingSearch || loadingQuality || loadingStability;

  // Aggregate model scores from all data sources
  const aggregatedModels = useMemo<AggregatedModelScore[]>(() => {
    const modelMap = new Map<string, AggregatedModelScore>();

    // Helper to get or create model entry
    const getModel = (modelId: string): AggregatedModelScore => {
      if (!modelMap.has(modelId)) {
        modelMap.set(modelId, {
          model: modelId,
          qualityScore: null,
          stabilityScore: null,
          winRate: null,
          avgCost: null,
          avgLatency: null,
          phase23WinRate: null,
          phase4WinRate: null,
          phase1Stability: null,
          phase4Stability: null,
          weightedScore: 0,
          confidence: 0,
        });
      }
      return modelMap.get(modelId)!;
    };

    // Process Phase 2+3 model comparison data
    if (modelComparison?.data?.models) {
      for (const m of modelComparison.data.models) {
        const model = getModel(m.model);
        const winRate = typeof m.winRate === 'string' ? parseFloat(m.winRate) : m.winRate;
        model.phase23WinRate = winRate;
        model.avgCost = m.totalCost / Math.max(m.totalExecutions, 1);
        model.avgLatency = (m.averageIndicationLatency + m.averageCriteriaLatency) / 2;
      }
    }

    // Process Phase 4 search model comparison data
    if (searchComparison?.data?.models) {
      for (const m of searchComparison.data.models) {
        const model = getModel(m.model);
        const winRate = typeof m.winRate === 'string' ? parseFloat(m.winRate) : m.winRate;
        model.phase4WinRate = winRate;
        // Average cost/latency from search if not set
        if (model.avgCost === null) {
          model.avgCost = m.averageCostUsd;
        } else {
          model.avgCost = (model.avgCost + m.averageCostUsd) / 2;
        }
        if (model.avgLatency === null) {
          model.avgLatency = m.averageLatencyMs;
        }
      }
    }

    // Process quality assessment data (prefer AI assessment for more data)
    const qualityModels = qualityData?.data?.aiAssessment?.modelPerformance
      || qualityData?.data?.modelPerformance
      || [];
    for (const m of qualityModels) {
      const model = getModel(m.model);
      model.qualityScore = m.averageQualityScore;
    }

    // Process stability data
    if (stabilityData?.data?.modelPerformance) {
      for (const m of stabilityData.data.modelPerformance) {
        const model = getModel(m.model);
        // Phase 1 stability (Case Analysis) - use AI score if available, else human verdict rate
        if (m.phase1.averageConsistencyScore !== null) {
          model.phase1Stability = m.phase1.averageConsistencyScore;
        } else {
          model.phase1Stability = calculateStabilityRate(m.phase1.verdictDistribution);
        }
        // Phase 2 stability (Search/Matching)
        if (m.phase2.averageConsistencyScore !== null) {
          model.phase4Stability = m.phase2.averageConsistencyScore;
        } else {
          model.phase4Stability = calculateStabilityRate(m.phase2.verdictDistribution);
        }
      }
    }

    // Calculate combined win rate and stability
    for (const model of modelMap.values()) {
      if (model.phase23WinRate !== null && model.phase4WinRate !== null) {
        model.winRate = (model.phase23WinRate + model.phase4WinRate) / 2;
      } else {
        model.winRate = model.phase23WinRate ?? model.phase4WinRate;
      }

      if (model.phase1Stability !== null && model.phase4Stability !== null) {
        model.stabilityScore = (model.phase1Stability + model.phase4Stability) / 2;
      } else {
        model.stabilityScore = model.phase1Stability ?? model.phase4Stability;
      }

      // Calculate confidence based on data availability
      let dataPoints = 0;
      let availablePoints = 0;
      if (model.qualityScore !== null) availablePoints++;
      if (model.phase1Stability !== null) availablePoints++;
      if (model.phase4Stability !== null) availablePoints++;
      if (model.phase23WinRate !== null) availablePoints++;
      if (model.phase4WinRate !== null) availablePoints++;
      if (model.avgCost !== null) availablePoints++;
      if (model.avgLatency !== null) availablePoints++;
      dataPoints = 7; // Total possible
      model.confidence = Math.round((availablePoints / dataPoints) * 100);
    }

    return Array.from(modelMap.values());
  }, [modelComparison, searchComparison, qualityData, stabilityData]);

  // Get ranked models for each phase
  const getPhaseRanking = (dataSource: 'phase23' | 'phase4' | 'combined') => {
    return [...aggregatedModels]
      .map(model => ({
        ...model,
        weightedScore: calculateWeightedScore(model, weights, dataSource),
      }))
      .sort((a, b) => b.weightedScore - a.weightedScore);
  };

  const selectedPhaseConfig = WORKFLOW_PHASES.find(p => p.id === selectedPhase);
  const phaseRanking = selectedPhaseConfig
    ? getPhaseRanking(selectedPhaseConfig.dataSource)
    : [];

  // Update weights
  const updateWeight = (key: keyof CriteriaWeights, value: number) => {
    setWeights(prev => ({ ...prev, [key]: value }));
  };

  // Reset to defaults
  const resetWeights = () => setWeights(DEFAULT_WEIGHTS);

  // Calculate total weight
  const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);

  // Build configuration for deploy modal
  const deployConfiguration = WORKFLOW_PHASES.map((phase) => {
    const ranking = getPhaseRanking(phase.dataSource);
    const recommended = ranking[0];
    return {
      phase: phase.name,
      model: recommended?.model || null,
      score: recommended ? calculateWeightedScore(recommended, weights, phase.dataSource) : 0,
    };
  });

  // Handle deploy
  const handleDeploy = async () => {
    setIsDeploying(true);
    // Simulate deployment delay
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsDeploying(false);
    setDeploySuccess(true);
    // Reset success state after closing modal
  };

  const handleCloseModal = () => {
    setShowDeployModal(false);
    // Reset success state after modal closes
    setTimeout(() => setDeploySuccess(false), 300);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production Workflow</h1>
          <p className="text-gray-500 mt-1">Loading analytics data...</p>
        </div>
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production Workflow</h1>
          <p className="text-gray-500 mt-1">
            Optimal model configuration based on evaluation data
          </p>
        </div>
        <button
          onClick={resetWeights}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Reset Weights
        </button>
      </div>

      {/* Workflow Visualization */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-gray-900">Recommended Workflow</h2>
        </div>

        <div className="flex items-center justify-center overflow-x-auto py-4">
          {WORKFLOW_PHASES.map((phase, idx) => {
            const ranking = getPhaseRanking(phase.dataSource);
            const recommended = ranking[0] || null;
            const score = recommended ? calculateWeightedScore(recommended, weights, phase.dataSource) : 0;

            return (
              <WorkflowNode
                key={phase.id}
                phase={phase}
                recommendedModel={recommended}
                score={score}
                isLast={idx === WORKFLOW_PHASES.length - 1}
                onClick={() => setSelectedPhase(phase.id)}
                isSelected={selectedPhase === phase.id}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <span>Phase 2+3 (Case Analysis)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>Phase 4 (Provider Matching)</span>
          </div>
          <div className="flex items-center gap-1">
            <Crown className="w-3 h-3 text-amber-500" />
            <span>Recommended Model</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Criteria Controls */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Settings2 className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Criteria Weights</h2>
          </div>

          <div className="space-y-5">
            <WeightSlider
              label="Quality Score"
              icon={Target}
              value={weights.quality}
              onChange={(v) => updateWeight('quality', v)}
              color="text-green-600"
            />
            <WeightSlider
              label="Stability"
              icon={Shield}
              value={weights.stability}
              onChange={(v) => updateWeight('stability', v)}
              color="text-blue-600"
            />
            <WeightSlider
              label="Win Rate"
              icon={Trophy}
              value={weights.winRate}
              onChange={(v) => updateWeight('winRate', v)}
              color="text-amber-600"
            />
            <WeightSlider
              label="Cost Efficiency"
              icon={DollarSign}
              value={weights.cost}
              onChange={(v) => updateWeight('cost', v)}
              color="text-purple-600"
            />
            <WeightSlider
              label="Latency"
              icon={Clock}
              value={weights.latency}
              onChange={(v) => updateWeight('latency', v)}
              color="text-red-600"
            />
          </div>

          {/* Total Weight Indicator */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Weight</span>
              <span className={clsx(
                'text-sm font-bold',
                totalWeight === 100 ? 'text-green-600' : 'text-amber-600'
              )}>
                {totalWeight}%
              </span>
            </div>
            {totalWeight !== 100 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Weights don't sum to 100% (normalized automatically)
              </p>
            )}
          </div>

          {/* Quick Presets */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-3">Quick Presets</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setWeights({ quality: 40, stability: 30, winRate: 20, cost: 5, latency: 5 })}
                className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Quality First
              </button>
              <button
                onClick={() => setWeights({ quality: 20, stability: 40, winRate: 20, cost: 10, latency: 10 })}
                className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Stability First
              </button>
              <button
                onClick={() => setWeights({ quality: 15, stability: 15, winRate: 20, cost: 30, latency: 20 })}
                className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cost Optimized
              </button>
              <button
                onClick={() => setWeights({ quality: 25, stability: 25, winRate: 25, cost: 12, latency: 13 })}
                className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Balanced
              </button>
            </div>
          </div>
        </div>

        {/* Phase Detail & Model Ranking */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              {selectedPhaseConfig && (
                <>
                  <selectedPhaseConfig.icon className="w-5 h-5 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedPhaseConfig.name} - Model Ranking
                  </h2>
                </>
              )}
            </div>
            <span className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {selectedPhaseConfig?.dataSource === 'phase4' ? 'Phase 4 Data' : 'Phase 2+3 Data'}
            </span>
          </div>

          {phaseRanking.length === 0 ? (
            <div className="text-center py-12">
              <Info className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No model data available</p>
              <p className="text-sm text-gray-400 mt-1">Run evaluations to collect performance data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {phaseRanking.map((model, idx) => (
                <ModelRankingCard
                  key={model.model}
                  rank={idx + 1}
                  model={model}
                  weights={weights}
                  phase={selectedPhaseConfig?.dataSource || 'combined'}
                  isRecommended={idx === 0}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Recommended Production Configuration</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {WORKFLOW_PHASES.map((phase) => {
            const ranking = getPhaseRanking(phase.dataSource);
            const recommended = ranking[0];
            const Icon = phase.icon;

            return (
              <div key={phase.id} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">{phase.name}</span>
                </div>
                {recommended ? (
                  <div className="flex items-center gap-2">
                    <ModelLogo model={recommended.model} size="sm" />
                    <span className="font-semibold text-gray-900">
                      {getModelName(recommended.model)}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">No recommendation</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-blue-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600">
              <Info className="w-4 h-4 inline mr-1 text-blue-500" />
              Based on {aggregatedModels.filter(m => m.confidence > 0).length} models with evaluation data
            </p>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">
                Avg confidence: {Math.round(aggregatedModels.reduce((a, m) => a + m.confidence, 0) / Math.max(aggregatedModels.length, 1))}%
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowDeployModal(true)}
            className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Rocket className="w-4 h-4" />
            Deploy to Production
          </button>
        </div>
      </div>

      {/* Deploy Modal */}
      <DeployModal
        isOpen={showDeployModal}
        onClose={handleCloseModal}
        onConfirm={handleDeploy}
        isDeploying={isDeploying}
        deploySuccess={deploySuccess}
        configuration={deployConfiguration}
        weights={weights}
      />
    </div>
  );
}
